import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';

import { Box } from '@nerdwallet/currency/components/Box';
import { useAnalytics } from '@lib/segment';
import {
  FinancialBankingReward,
  FinancialCreditCardReward,
  FinancialNwPlusRewardProductEnum,
  RewardableEventType,
} from '@generated-gql/generated-types';

import SegmentImpression, {
  ImpressionType,
} from '@components/segment-impression';
import { modalsEnum } from '@components/modal-manager/modals/constants';
import PlaidModal from '@components/financial-institutions/plaid-sandbox/plaid-modal';
import NerdwalletPlusRewardFooterBox from '@components/modal-manager/modals/nerdwallet-plus/footer-tracker';
import { showModal } from '@lib/redux/actions/modals';

import RichRewardStepTile from '../reward-step-tile/rich-reward-step-tile';
import styles from './rewards-tracker.module.scss';
import {
  getDefaultAnalyticsProps,
  getOfferDisplayName,
  useActions,
  useTrackerSteps,
} from './utils';
import { useRewardsPoints } from '@lib/hooks/nerdwallet-plus';
import { When } from 'react-if';
import { usePlaidModalVisibility } from '@components/financial-institutions/plaid-sandbox/plaid-modal-visibility-context';
import RewardTrackerHeader from '../reward-tracker-header';

type ModalTriggerCallback = (params: {
  onSuccess: () => void;
  defaultSegmentTrackerProps?: any;
  extraInfo?: {
    completedTasks?: number;
    totalTasks?: number;
    offerDisplayName?: string;
  };
  isCreditCard: boolean;
}) => void;

interface Props {
  reward: FinancialBankingReward | FinancialCreditCardReward;
  onShowDismissRewardModal: ModalTriggerCallback;
  refetchRewards?: () => void;
}

const RewardsTracker: React.FC<Props> = ({
  reward,
  onShowDismissRewardModal,
  refetchRewards,
}) => {
  const { showPlaidVisibility } = usePlaidModalVisibility();
  const [showPlaidModal, setShowPlaidModal] = useState(false);

  const isCreditCard =
    reward.productType === FinancialNwPlusRewardProductEnum.CreditCards;

  const eventType = isCreditCard
    ? RewardableEventType.PaidEligibleCreditCardOnTime
    : RewardableEventType.SavingsAccountFunded;

  const { data } = useRewardsPoints<any>([
    RewardableEventType.PaidEligibleCreditCardOnTime,
    RewardableEventType.SavingsAccountFunded,
  ]);

  const points = data?.[eventType];

  const { segmentTrack } = useAnalytics();
  const defaultSegmentTrackerProps = getDefaultAnalyticsProps(reward);
  const offerDisplayName = getOfferDisplayName(reward);

  const {
    refetchPlaidLinkedAccounts,
    updateRewardAsDismissed,
    handleOnLinkAccount,
  } = useActions();

  const {
    initialStep,
    selectedStep,
    setSelectedStep,
    currentStep,
    amountOfSteps,
    amountOfCompletedSteps,
  } = useTrackerSteps(reward);

  const segmentTrackerProps = {
    ...defaultSegmentTrackerProps,
    total_tasks: amountOfSteps,
    current_tasks: amountOfSteps - amountOfCompletedSteps,
    current_task_status: reward.steps[initialStep]?.analyticsStatus,
    cta_location: 'nerdwallet+',
  };

  const extraInfo = {
    offerDisplayName: offerDisplayName,
    totalTasks: amountOfSteps,
    completedTasks: amountOfCompletedSteps,
  };

  const handleDismissBoxClick = () => {
    segmentTrack('NW Plus Rewards Tracker Secondary CTA Clicked', {
      ...segmentTrackerProps,
      cta_copy: 'why_see_tracker',
    });
    onShowDismissRewardModal({
      onSuccess: () => {
        updateRewardAsDismissed({
          variables: {
            id: reward.id,
          },
          onCompleted: () => {
            refetchRewards?.();
          },
        });
      },
      defaultSegmentTrackerProps,
      extraInfo: extraInfo,
      isCreditCard,
    });
  };

  const handleCtaClick = () => {
    segmentTrack('NW Plus Rewards Tracker Primary CTA Clicked', {
      ...segmentTrackerProps,
      cta_copy: currentStep?.ctaText.text?.toUpperCase(),
    });
    handleOnLinkAccount();
    setShowPlaidModal(true);
  };

  useEffect(() => {
    if (!showPlaidVisibility.show && showPlaidModal) {
      setShowPlaidModal(false);
    }
  }, [showPlaidVisibility, showPlaidModal]);

  return (
    <SegmentImpression
      eventType={ImpressionType.TRACK}
      eventName="NW Plus Reward Task Tracker Module Viewed"
      eventProps={segmentTrackerProps}
    >
      <When condition={showPlaidModal}>
        <PlaidModal refetchPlaidLinkedAccounts={refetchPlaidLinkedAccounts} />
      </When>
      <div className={styles.container}>
        <div className={styles.container}>
          <RewardTrackerHeader
            headerText={offerDisplayName}
            eventType={eventType}
            points={points}
            amountOfSteps={amountOfSteps}
            amountOfCompletedSteps={amountOfCompletedSteps}
            displayCondition={
              amountOfSteps !== amountOfCompletedSteps && !!points
            }
          />
          <Box paddingTop="3">
            {reward.steps?.map((step, index) => {
              const isExpanded = selectedStep === index;

              return (
                <RichRewardStepTile
                  key={step.title.text}
                  step={step}
                  isExpanded={isExpanded}
                  onSelectStep={() => {
                    setSelectedStep(isExpanded ? null : index);
                  }}
                  onCtaClick={handleCtaClick}
                />
              );
            })}
          </Box>
        </div>
        <NerdwalletPlusRewardFooterBox
          onClick={handleDismissBoxClick}
          footerText={"I don't need this"}
        />
      </div>
    </SegmentImpression>
  );
};

const mapDispatchToProps = (dispatch) => ({
  onShowDismissRewardModal: ({
    onSuccess,
    defaultSegmentTrackerProps,
    extraInfo,
    isCreditCard,
  }) =>
    dispatch(
      showModal({
        modalType: modalsEnum.NERDWALLET_PLUS_REWARD_DISMISS,
        props: {
          onSuccess,
          isCreditCard,
          defaultSegmentTrackerProps,
          extraInfo,
        },
      }),
    ),
});

export default connect(null, mapDispatchToProps)(RewardsTracker);
